import React from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Chip, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { formatCurrency } from '../utils/pricing';
import { Product } from '../types/api';
import { calculateFees } from '../utils/pricing';
import AddToCartButton from './AddToCartButton';
import FavoriteButton from './FavoriteButton';

interface ProductCardProps {
  product: Product;
  showDescription?: boolean;
  compact?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, showDescription = false, compact = false }) => {
  const {
    id,
    name,
    price,
    compareToPrice,
    description,
    imageUrl,
    thumbnailUrl,
    inStock = true,
    showOnSale,
    categoryIds = []
  } = product;
  
  // Check if product is on sale
  const isOnSale = compareToPrice && compareToPrice > price;
  
  // Calculate discount percentage
  const discountPercentage = isOnSale && compareToPrice 
    ? Math.round(((compareToPrice - price) / compareToPrice) * 100) 
    : 0;
  
  // Calculate fees
  const fees = calculateFees(product);
  const { total } = fees;
  
  // Determine if product is eligible for free shipping
  const hasFreeShipping = price >= 99;
  
  const truncateDescription = (text: string, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength 
      ? text.substring(0, maxLength) + '...' 
      : text;
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        '&:hover': {
          transform: compact ? 'translateY(-3px)' : 'translateY(-5px)',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      {/* Product Image */}
      <Box 
        component={RouterLink} 
        to={`/product/${id}`}
        sx={{ 
          position: 'relative', 
          pt: compact ? '80%' : '100%', // Smaller height ratio for compact mode
          display: 'block',
          textDecoration: 'none'
        }}
      >
        <CardMedia
          component="img"
          image={imageUrl || thumbnailUrl || '/placeholder.png'}
          alt={name}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: compact ? 1 : 2, // Smaller padding for compact mode
            bgcolor: 'white',
            opacity: !inStock ? 0.6 : 1, // Dim out-of-stock items
            filter: !inStock ? 'grayscale(30%)' : 'none', // Slight grayscale for out-of-stock
          }}
        />
        
        {/* Out-of-stock overlay */}
        {!inStock && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              zIndex: 1,
            }}
          />
        )}
        {/* Sale badge - show in both compact and regular modes */}
        {isOnSale && (
          <Chip
            label="On Sale"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              fontWeight: 600,
              fontSize: compact ? '0.7rem' : '0.75rem',
              zIndex: 2,
              bgcolor: '#4CAF50',
              color: 'white',
              '&:hover': {
                bgcolor: '#45a049'
              }
            }}
          />
        )}
        
        {/* Favorite button - hide in compact mode */}
        {!compact && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 10, 
              left: 10,
              zIndex: 1
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <FavoriteButton 
              productId={id} 
              initialFavorite={product.is_favorite} 
              size="medium"
            />
          </Box>
        )}
        
        {/* Status indicators */}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: 'absolute',
            bottom: compact ? 6 : 10,
            left: compact ? 6 : 10,
          }}
        >
          {hasFreeShipping && !compact && (
            <Chip
              icon={<LocalShippingOutlinedIcon fontSize="small" />}
              label="Free Shipping"
              size="small"
              sx={{
                bgcolor: 'rgba(46, 125, 50, 0.1)',
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '0.7rem',
              }}
            />
          )}
          
          {/* Always show out of stock indicator */}
          {!inStock && (
            <Chip
              label="Out of Stock"
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: compact ? '0.65rem' : '0.7rem',
                zIndex: 2,
                bgcolor: '#f44336',
                color: 'white',
                '&:hover': {
                  bgcolor: '#d32f2f'
                }
              }}
            />
          )}
        </Stack>
      </Box>
      
      {/* Product Details */}
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        p: compact ? 1 : 2,
        pb: compact ? 1 : 2 // Override Material UI's default padding bottom
      }}>
        <Box component={RouterLink} to={`/product/${id}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography
            variant={compact ? "body1" : "h6"}
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              fontSize: compact ? '0.8rem' : '0.9rem',
              lineHeight: 1.2,
              mb: compact ? 0.5 : 1,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              height: compact ? '2.8rem' : '3.6rem',
              color: 'text.primary'
            }}
          >
            {name}
          </Typography>
        </Box>
        
        {/* Price section */}
        <Box sx={{ mb: compact ? 1 : 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography 
              variant={compact ? "body1" : "h6"}
              color="primary.main"
              sx={{ fontWeight: 700, fontSize: compact ? '0.875rem' : 'inherit' }}
            >
              {formatCurrency(price)}
            </Typography>
            
            {isOnSale && compareToPrice && (
              <Typography 
                variant="body2" 
                sx={{ 
                  textDecoration: 'line-through',
                  color: 'text.secondary',
                  fontSize: compact ? '0.75rem' : 'inherit'
                }}
              >
                {formatCurrency(compareToPrice)}
              </Typography>
            )}
          </Stack>
        </Box>
        
        {/* Description (optional) */}
        {showDescription && description && !compact && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              height: '3rem'
            }}
          >
            {truncateDescription(description, 120)}
          </Typography>
        )}
        
        {/* Add to cart button - hide in compact mode */}
        {!compact && (
          <Box sx={{ mt: 'auto' }}>
            <AddToCartButton 
              product={product}
              variant="outlined"
              buttonSize="small"
              fullWidth
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductCard;